import React from 'react';
import { Box, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import { Building2, Mail, Users } from 'lucide-react';
import { Primary, Secondary } from 'ui-components/Buttons';
import CustomInputField from 'ui-components/CustomInputField';
import { navigateToDomain } from 'utilities/index';
import { INDIGO, INDIGO_SOFT, TEXT_COLOR } from './fields';

const SUPPORT_EMAIL = 'support@backrr.com';

// Copy per plan, from the "Same Email Domain & Collaborator Limits" story.
const limitCopy = (plan) => {
  if (plan === 'pro') {
    return { message: 'This startup profile has reached its collaborator limit.', contactSales: true };
  }
  if (plan === 'plus') {
    return { message: 'This startup profile has reached its collaborator limit. Upgrade to Pro to add upto 10 team members.' };
  }
  return { message: 'This startup profile has reached its collaborator limit. Upgrade to Backrr Plus or Pro to add more team members.' };
};

const StartupLogo = ({ startup }) =>
  startup?.logo_url ? (
    <img
      src={startup.logo_url}
      alt={startup.startup_name}
      style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', border: '1px solid #EFEFF2' }}
    />
  ) : (
    <Box sx={{ width: 48, height: 48, borderRadius: '12px', background: INDIGO_SOFT, color: INDIGO, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Building2 size={22} />
    </Box>
  );

const LimitNotice = ({ plan }) => {
  const { message, contactSales } = limitCopy(plan);
  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', background: '#FFF8EB', border: '1px solid #FDE7C2' }}>
      <Typography sx={{ fontSize: 14, color: '#7A4B00' }}>{message}</Typography>
      {contactSales ? (
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: 13, color: '#7A4B00', mb: 1 }}>Need more seats? Our sales team can help.</Typography>
          <Secondary component="a" href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('More team members on Backrr Pro')}`} sx={{ display: 'inline-flex', gap: 1, px: 2, py: 1 }}>
            <Mail size={16} /> Contact Sales
          </Secondary>
        </Box>
      ) : (
        <Typography
          component="a"
          href={navigateToDomain('main', 'pricing')}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-block', mt: 1, fontSize: 14, fontWeight: 600, color: INDIGO, textDecoration: 'none' }}
        >
          View pricing
        </Typography>
      )}
    </Box>
  );
};

// Mirrors assertCanCreateStartup: no new profile while a request waits, or when every
// startup still open to this user is full. A declined startup no longer counts.
export const canCreateProfile = (startups = []) => {
  if (startups.some((startup) => startup.request_status === 'pending')) return false;
  const open = startups.filter((startup) => startup.request_status !== 'rejected');
  return open.length === 0 || open.some((startup) => !startup.is_full);
};

const introCopy = (startups, canCreateNew) => {
  if (startups.some((startup) => startup.request_status === 'pending')) {
    return 'The owner has been told. You will get access as soon as they approve.';
  }
  if (startups.every((startup) => startup.request_status === 'rejected')) {
    return 'You can create your own startup profile instead.';
  }
  return canCreateNew
    ? 'Ask to join as a collaborator instead of creating a duplicate profile.'
    : 'Ask its owner to upgrade so you can join.';
};

// The owner decides on this name, so a request always carries one.
const RequestNameDialog = ({ startup, initialName, onClose, onSubmit }) => {
  const [firstName, setFirstName] = React.useState(initialName?.first_name || '');
  const [lastName, setLastName] = React.useState(initialName?.last_name || '');
  const [showErrors, setShowErrors] = React.useState(false);
  const first_name = firstName.trim();
  const last_name = lastName.trim();

  const submit = (event) => {
    event.preventDefault();
    if (!first_name || !last_name) {
      setShowErrors(true);
      return;
    }
    onSubmit({ first_name, last_name });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box component="form" onSubmit={submit} noValidate>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: '0.5rem' }}>
            Request to join {startup.startup_name}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#667085', lineHeight: 1.5, mb: '1rem' }}>
            Your name is shown to the owner so they know who is asking.
          </Typography>
          <CustomInputField
            id="domain-join-first-name"
            autoFocus
            label="First Name"
            placeholder="Enter first name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            hasError={showErrors && !first_name}
            errorMessage="First name is required"
            inputProps={{ maxLength: 100 }}
          />
          <CustomInputField
            id="domain-join-last-name"
            label="Last Name"
            placeholder="Enter last name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            hasError={showErrors && !last_name}
            errorMessage="Last name is required"
            inputProps={{ maxLength: 100 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', mt: '1.25rem' }}>
            <Secondary
              type="button"
              onClick={onClose}
              sx={{ borderRadius: '10px', fontSize: '0.875rem', padding: '0.5rem 1.25rem' }}
            >
              Cancel
            </Secondary>
            <Primary
              type="submit"
              sx={{ borderRadius: '10px', fontSize: '0.875rem', padding: '0.5rem 1.25rem' }}
            >
              Send request
            </Primary>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Shown on onboarding step 1 when startups on the user's company domain exist.
 * Joining needs the owner's approval. When every match is full there is no "create"
 * way out: that is the bypass the limit closes.
 */
const DomainStartupPrompt = ({ startups, busyId, requesterName, onRequest, onCancel, onCreateNew }) => {
  const canCreateNew = canCreateProfile(startups);
  const [namingStartup, setNamingStartup] = React.useState(null);

  return (
    <Box>
      {namingStartup && (
        <RequestNameDialog
          startup={namingStartup}
          initialName={requesterName}
          onClose={() => setNamingStartup(null)}
          onSubmit={(name) => {
            onRequest(namingStartup.startup_id, name);
            setNamingStartup(null);
          }}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: INDIGO_SOFT, color: INDIGO, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Users size={20} />
        </Box>
        <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: TEXT_COLOR }}>
          Your team is already on Backrr
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, color: '#667085', mb: 3 }}>
        {startups.length > 1 ? 'These startups use' : 'This startup uses'} your company email domain.{' '}
        {introCopy(startups, canCreateNew)}
      </Typography>

      {startups.map((startup) => (
        <Box key={startup.startup_id} sx={{ p: 2, mb: 2, border: '1px solid #EFEFF2', borderRadius: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <StartupLogo startup={startup} />
            <Typography sx={{ flex: 1, minWidth: 140, fontSize: 16, fontWeight: 600, color: TEXT_COLOR }}>
              {startup.startup_name}
            </Typography>
            {startup.request_status === 'pending' ? (
              <Secondary
                onClick={busyId ? undefined : () => onCancel(startup.startup_id)}
                disabled={!!busyId}
                sx={{ px: 3, py: 1.2, minWidth: 150 }}
              >
                {busyId === startup.startup_id ? <CircularProgress size={18} /> : 'Cancel request'}
              </Secondary>
            ) : !startup.request_status && !startup.is_full && (
              <Primary
                onClick={busyId ? undefined : () => setNamingStartup(startup)}
                disabled={!!busyId}
                sx={{ px: 3, py: 1.2, minWidth: 150 }}
              >
                {busyId === startup.startup_id ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Request to join'}
              </Primary>
            )}
          </Box>
          {startup.request_status === 'pending' && (
            <Typography sx={{ mt: 1.5, fontSize: 14, fontWeight: 600, color: INDIGO }}>Waiting for owner approval</Typography>
          )}
          {startup.request_status === 'rejected' && (
            <Typography sx={{ mt: 1.5, fontSize: 14, color: '#667085' }}>The owner declined your request. Ask them to invite you directly.</Typography>
          )}
          {!startup.request_status && startup.is_full && <LimitNotice plan={startup.plan} />}
        </Box>
      ))}

      {canCreateNew && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography
            component="button"
            type="button"
            onClick={onCreateNew}
            disabled={!!busyId}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#475467', textDecoration: 'underline' }}
          >
            Create a new startup profile instead
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DomainStartupPrompt;
